import React, { memo } from 'react';
import _isEmpty from 'lodash/isEmpty';
import type { DrawerBlockStateType } from '@unifyapps/defs/blocks/Drawer/types';
import type { ModalBlockStateType } from '@unifyapps/defs/blocks/Modal/types';
import type { ContextualDialogBlockStateType } from '@unifyapps/defs/blocks/ContextualDialog/types';
import type { BlockRendererProps } from '../../RegistryProvider/types';
import { useCanAccess } from '../../../access-control/hooks/useCanAccess';
import useIsBlockVisibility from '../../../hooks/useIsBlockVisibility';
import useGetBlockBehavior from '../../../hooks/useGetBlockBehavior';

type DetachableBlockState =
  | DrawerBlockStateType
  | ModalBlockStateType
  | ContextualDialogBlockStateType;

export const withVisibilityPermissionsAndDetachedCheck = (
  WrappedComponent: React.ComponentType<BlockRendererProps>,
) => {
  function WithCombinedChecksWrapper(props: BlockRendererProps) {
    const { block, context, blockState } = props;

    const isBlockPermissible = useCanAccess(block.permissions);
    const { getIsBlockVisibility } = useIsBlockVisibility();
    const isDetachedBlock = useGetBlockBehavior(block.component.componentType)?.detachFromLayout;

    if (!isBlockPermissible) {
      return null;
    }

    if (
      isDetachedBlock &&
      !_isEmpty(blockState) &&
      !(blockState as DetachableBlockState).state.open
    ) {
      return null;
    }

    const isVisible = getIsBlockVisibility({
      visible: block.visibility,
      context,
    });

    if (!isVisible) {
      return null;
    }

    return <WrappedComponent {...props} />;
  }

  return memo(WithCombinedChecksWrapper);
};
